/**
 * Gäste-Tutorial
 *
 * Führt durch die Teilnehmer, die nicht regulär im Kurs angemeldet sind:
 * Gäste erfassen und Mitglieder aus anderen Kursen hinzunehmen.
 *
 * Abgedeckte Prozesse: P10 (Gäste), P11 (Mitglied hinzufügen), P13 (entfernen)
 * — siehe docs/PROZESSLANDKARTE.md.
 */

import type { TutorialDefinition, TutorialStep } from './types'
import { useCoursesStore } from '@/stores/courses.store'
import { useSessionsStore } from '@/stores/sessions.store'

function createSteps(): TutorialStep[] {
  const coursesStore = useCoursesStore()
  const sessionsStore = useSessionsStore()

  const firstCourse = coursesStore.courses[0]
  const session = firstCourse ? sessionsStore.getMostRelevantSession(firstCourse.id) : undefined

  const steps: TutorialStep[] = [
    {
      popover: {
        title: 'Gäste und zusätzliche Mitglieder',
        description:
          'Manchmal steht jemand im Training, der nicht im Kurs angemeldet ist. Diese Anleitung zeigt dir die zwei Wege, ihn trotzdem zu erfassen.',
      },
    },
  ]

  if (!firstCourse || !session) {
    steps.push({
      popover: {
        title: 'Noch kein Termin vorhanden',
        description:
          'Sobald du einen Kurs mit Terminen hast, kannst du dort Gäste und zusätzliche Mitglieder eintragen.',
      },
    })
    return steps
  }

  steps.push(
    {
      navigateTo: `/course/${firstCourse.id}/session/${session.id}/attendance`,
      element: '[data-tutorial="guest-section"]',
      popover: {
        title: 'Gäste eintragen',
        description:
          'Für Personen ohne Mitgliedschaft: Vor- und Nachname genügen, E-Mail und Telefonnummer sind freiwillig. Gäste zählen automatisch als anwesend.',
        position: 'top',
      },
    },
    {
      element: '[data-tutorial="add-member"]',
      popover: {
        title: 'Mitglieder aus anderen Kursen',
        description:
          'Ist die Person bereits Vereinsmitglied, nimm diesen Weg. Du suchst sie nach Name oder Mitgliedsnummer; wer im Kurs schon angemeldet ist, wird dabei ausgeblendet.',
        position: 'left',
      },
    },
    {
      element: '[data-tutorial="attendance-list"]',
      popover: {
        title: 'Zusätzliche Mitglieder in der Liste',
        description:
          'Hinzugefügte Mitglieder stehen in einem eigenen Abschnitt unter den regulären Teilnehmern. Kommt jemand dauerhaft, kannst du ihn dort fest übernehmen.',
        position: 'top',
      },
    },
    {
      popover: {
        title: 'Wieder entfernen',
        description:
          'Zum Entfernen wischst du einen Eintrag nach links (am Rechner: gedrückt halten). Bereits erfasste Anwesenheiten bleiben dabei erhalten.',
      },
    },
    {
      popover: {
        title: 'Zwei Unterschiede zum Merken',
        description:
          'Gäste kannst du auch ohne Internetverbindung anlegen — sie werden später übertragen. Mitglieder hinzufügen und entfernen geht nur online.',
      },
    }
  )

  return steps
}

export const guestsTutorial: TutorialDefinition = {
  id: 'guests',
  title: 'Gäste und zusätzliche Mitglieder',
  description: 'Personen erfassen, die nicht regulär im Kurs angemeldet sind.',
  icon: 'pi pi-user-plus',
  steps: createSteps,
}
